<template>
  <div class="ida-academy-homepage">
    <!-- Welcome Section -->
    <div class="welcome-section mb-4">
      <div class="card shadow-sm">
        <div class="card-body p-4">
          <h2 class="mb-3 text-primary">{{ $t('ida-academy.welcome-title') }}</h2>
          <p class="lead mb-3">{{ $t('ida-academy.welcome-subtitle') }}</p>
          <p class="mb-2">{{ $t('ida-academy.welcome-paragraph-1') }}</p>
          <p class="mb-2">{{ $t('ida-academy.welcome-paragraph-2') }} </p>
          <p class="mb-0">{{ $t('ida-academy.welcome-paragraph-3') }} </p>
        </div>
      </div>
    </div>

    <!-- Branded Networks Section -->
    <div class="branded-networks-section">
      <div class="card shadow-sm">
        <div class="card-body">
          <!-- Not Signed In State -->
          <div v-if="!$auth.auth" class="text-center py-5">
            <i class="fas fa-lock fa-3x text-primary mb-3"></i>
            <h5 class="mb-3">{{ $t('ida-academy.sign-in-to-explore') }}</h5>
            <button
              class="btn btn-primary btn-lg"
              @click="showSignIn"
            >
              <i class="fas fa-sign-in-alt me-2"></i>
              {{ $t('Nav.sign-in') }}
            </button>
          </div>

          <!-- Signed In State -->
          <template v-else>
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5 class="card-title mb-0">{{ $t('branded-networks') }}</h5>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{ $t('loading-networks') }}</span>
              </div>
              <p class="text-muted mt-2">{{ $t('loading-branded-networks') }}</p>
            </div>

            <!-- Networks Grid -->
            <div v-else-if="networks.length > 0" class="row g-3">
            <div
              v-for="network in networks"
              :key="network.id"
              class="col-lg-4 col-md-6 col-sm-12"
            >
              <div class="network-card card h-100 card-hover">
                <router-link
                  :to="`/networks/${network.slug}`"
                  class="text-decoration-none"
                >
                  <div class="network-cover-wrapper">
                    <img
                      v-if="network.cover_url"
                      :src="network.cover_url"
                      :alt="network.name"
                      class="network-cover"
                    >
                    <div v-else class="network-cover-placeholder">
                      <i class="fas fa-network-wired fa-3x text-muted"></i>
                    </div>
                  </div>
                  <div class="card-body">
                    <div class="d-flex align-items-start mb-2">
                      <img
                        v-if="network.logo_url"
                        :src="network.logo_url"
                        :alt="network.name"
                        class="network-logo me-2"
                      >
                      <div class="flex-grow-1">
                        <h6 class="network-name text-dark mb-1">{{ network.name }}</h6>
                        <p v-if="network.short_name" class="text-muted small mb-0">
                          {{ network.short_name }}
                        </p>
                      </div>
                    </div>
                    <p v-if="network.description" class="network-description text-secondary small mb-3">
                      {{ truncateDescription(network.description) }}
                    </p>
                    <div class="network-stats d-flex justify-content-between text-muted small">
                      <span>
                        <i class="fas fa-users me-1"></i>
                        {{ network.num_users || 0 }} {{ $t('members') }}
                      </span>
                      <span>
                        <i class="fas fa-photo-video me-1"></i>
                        {{ network.media_count || 0 }} {{ $t('media') }}
                      </span>
                    </div>
                  </div>
                </router-link>
              </div>
            </div>
            </div>

            <!-- Empty State -->
            <div v-else class="text-center py-5">
              <i class="fas fa-network-wired fa-3x text-muted mb-3"></i>
              <p class="text-muted">{{ $t('no-networks-available') }}</p>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';

export default {
  name: 'IdaAcademyHomepage',
  data() {
    return {
      networks: [],
      loading: false,
    };
  },
  computed: {
    ...mapGetters(['currentBrand']),
  },
  mounted() {
    // Only load networks if user is authenticated
    if (this.$auth.auth) {
      this.loadBrandedNetworks();
    }
  },
  methods: {
    showSignIn() {
      this.$bus.emit('go-to-sign-in');
    },
    async loadBrandedNetworks() {
      if (!this.currentBrand || !this.currentBrand.id) {
        console.warn('No current brand available');
        return;
      }

      this.loading = true;
      try {
        const response = await this.$http.get('/networks/all', {
          params: {
            brand_id: this.currentBrand.id,
          },
        });

        // Combine all networks from different categories
        const allNetworks = [
          ...(response.data.myManagedNetworks || []),
          ...(response.data.myNetworks || []),
          ...(response.data.discoverNetworks || []),
        ];

        // Filter to only show networks that match the current brand
        this.networks = allNetworks.filter(network => {
          return network.brand_id === this.currentBrand.id;
        });

        // Sort by number of users (most popular first)
        this.networks.sort((a, b) => (b.num_users || 0) - (a.num_users || 0));
      } catch (error) {
        alert(this.$t('error-loading-networks'));
        this.networks = [];
      } finally {
        this.loading = false;
      }
    },
    truncateDescription(description) {
      if (!description) return '';
      const maxLength = 120;
      if (description.length <= maxLength) return description;
      return description.substring(0, maxLength) + '...';
    },
  },
};
</script>
<style scoped>
.ida-academy-homepage {
  padding: 1rem 2rem;
}
.welcome-section .card {
  border-left: 4px solid var(--bs-primary);
}
.welcome-section h2 {
  font-weight: 600;
}
.welcome-section .lead {
  font-size: 1.15rem;
  font-weight: 500;
  color: #333;
}
.network-card {
  transition: all 0.2s ease;
  border: 1px solid #e9ecef;
}
.network-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}
.network-cover-wrapper {
  position: relative;
  width: 100%;
  height: 150px;
  overflow: hidden;
  background-color: #f8f9fa;
}
.network-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.network-cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}
.network-logo {
  width: 50px;
  height: 50px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid #e9ecef;
  background-color: #fff;
  padding: 4px;
}
.network-name {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.3;
}
.network-description {
  line-height: 1.4;
  min-height: 2.8em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.network-stats {
  padding-top: 0.5rem;
  border-top: 1px solid #f0f0f0;
}
.custom-border {
  border: 3px solid;
}
.card-hover {
  transition: all 0.2s ease;
}
@media (max-width: 768px) {
  .ida-academy-homepage {
    padding: 1rem;
  }
  .welcome-section .card-body {
    padding: 1.5rem !important;
  }
}
</style>
